import { Upload as AntUpload, type UploadProps as AntUploadProps, Input } from 'antd'
import type { UploadFile } from 'antd/es/upload'
import { FileIcon, Loader2Icon, Trash2, UploadIcon } from 'lucide-react'
import pluralize from 'pluralize'
import { FC, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { UPLOAD_ROOT_TEST_ID } from './consts'
import { UPLOAD_LABELS } from './labels'

const { Dragger } = AntUpload

export interface FileWithStatus extends UploadFile {
  isEncrypted?: boolean
  status?: 'uploading' | 'done' | 'error' | 'removed'
  error?: { message: string }
  response?: string
}

export interface UploadProps extends AntUploadProps {
  disabled?: boolean
  fileList?: FileWithStatus[]
  defaultFileList?: FileWithStatus[]
  filePasswords?: Record<string, string>
  onPasswordChange?: (passwords: Record<string, string>) => void
  hideList?: boolean
  isUploading?: boolean
  supportedFormatsHint?: string
  appearance?: 'dropzone' | 'button'
  buttonLabel?: string
}

export const Upload: FC<UploadProps> = ({
  disabled,
  fileList: externalFileList,
  onRemove,
  filePasswords = {},
  onPasswordChange,
  defaultFileList = [],
  hideList = false,
  className,
  isUploading = false,
  supportedFormatsHint,
  appearance = 'dropzone',
  buttonLabel = 'Upload files',
  ...props
}) => {
  const [internalFileList, setInternalFileList] = useState<FileWithStatus[]>(defaultFileList)
  const fileList = !externalFileList ? internalFileList : externalFileList

  const handlePasswordChange = (fileUid: string, password: string) => {
    if (onPasswordChange) {
      onPasswordChange({
        ...filePasswords,
        [fileUid]: password,
      })
    }
  }

  const handleRemove = (file: FileWithStatus) => {
    if (!externalFileList) {
      setInternalFileList((prev) => prev.filter((f) => f.uid !== file.uid))
    }
    onRemove?.(file)
  }

  return (
    <div
      data-testid={UPLOAD_ROOT_TEST_ID}
      className={cn('w-full rounded-lg', disabled && 'pointer-events-none', className)}
    >
      {appearance === 'button' ? (
        <AntUpload
          {...props}
          disabled={disabled}
          fileList={fileList}
          showUploadList={false}
          onChange={(info) => {
            if (!externalFileList) {
              setInternalFileList(info.fileList)
            }
            props.onChange?.(info)
          }}
          onRemove={handleRemove}
        >
          <Button disabled={disabled} variant='outline'>
            <TextWithIcon icon={<UploadIcon className='size-4' />} text={buttonLabel} />
          </Button>
        </AntUpload>
      ) : (
        <Dragger
          {...props}
          disabled={disabled}
          fileList={fileList}
          showUploadList={false}
          onChange={(info) => {
            if (!externalFileList) {
              setInternalFileList(info.fileList)
            }
            props.onChange?.(info)
          }}
          onRemove={handleRemove}
        >
          {isUploading ? (
            <div className='pointer-events-none flex min-h-36 flex-col items-center justify-center gap-2 px-16 py-6'>
              <Loader2Icon className='size-8 animate-spin text-main-foreground-primary' />
              <p className='text-main-foreground-primary text-body-default'>
                {UPLOAD_LABELS.uploadingFiles}
              </p>
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center gap-2 px-16 py-6'>
              <div className='flex size-8 items-center justify-center rounded-full bg-main-background-subtle-gray'>
                <UploadIcon
                  className={cn(
                    'size-5',
                    disabled ? 'text-main-foreground-disabled' : 'text-main-foreground-primary',
                  )}
                />
              </div>
              <div className='flex flex-col items-center gap-4'>
                <div className='flex flex-col items-center gap-2'>
                  <p
                    className={cn(
                      'text-label-default',
                      disabled ? 'text-main-foreground-disabled' : 'text-main-foreground-primary',
                    )}
                  >
                    {UPLOAD_LABELS.dragAndDrop}
                  </p>
                  <Button disabled={disabled} variant='outline'>
                    Select {pluralize('file', props.maxCount || 2)}
                  </Button>
                </div>
                {supportedFormatsHint && (
                  <p className='text-center text-main-foreground-tertiary text-body-small'>
                    {supportedFormatsHint}
                  </p>
                )}
              </div>
            </div>
          )}
        </Dragger>
      )}
      {!hideList && fileList && fileList.length > 0 && (
        <div className='mt-2 space-y-2'>
          {fileList.map((file) => {
            const hasErrors =
              file.status === 'error' && file.response !== UPLOAD_LABELS.notFoundResponse

            return (
              <div
                key={file.uid}
                className={
                  'group flex items-start justify-between border-b border-main-border-default px-2 py-2 transition-colors'
                }
              >
                <div className='flex min-w-0 flex-1 items-start gap-1.5'>
                  <FileIcon className={'mt-0.5 size-4 shrink-0 text-main-foreground-tertiary'} />
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                      <span
                        className={cn(
                          'truncate text-body-default',
                          hasErrors
                            ? 'text-main-foreground-danger'
                            : 'text-main-foreground-primary',
                        )}
                      >
                        {file.name}
                      </span>
                      {file.isEncrypted && (
                        <Input.Password
                          className='w-32'
                          placeholder={UPLOAD_LABELS.passwordPlaceholder}
                          size='small'
                          value={filePasswords[file.uid] || ''}
                          onChange={(e) => handlePasswordChange(file.uid, e.target.value)}
                        />
                      )}
                    </div>
                    {hasErrors && file.error && (
                      <span className='mt-1 block text-main-foreground-danger text-body-small'>
                        {file.error.message || UPLOAD_LABELS.uploadFailed}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size={'icon-sm'}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(file)
                  }}
                  tabIndex={-1}
                  aria-label={UPLOAD_LABELS.deleteFile}
                  className='invisible !bg-main-background-interactive-secondary-default group-hover:visible'
                >
                  <Trash2 className='size-4 text-main-foreground-primary' />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
